<template>
    <Head title="Edit Note" />
    <div class="overflow-y-hidden md:pb-10 pt-4 md:bg-surface">
        <BaseLink type="button" class="flex gap-2 items-center text-ink-muted ml-6 lg:ml-10 w-fit" :href="connection_detail_url ?? connections_page_url" preserve-state>
            <i class="bi bi-chevron-left icon-bold" aria-hidden="true"></i> <span class="body-small">Back</span>
        </BaseLink>
        <div class="mx-auto max-w-300 flex flex-col gap-4 md:gap-10 px-4 lg:px-10 py-6 h-dvh">
            <div class="flex items-center justify-between">
                <BasePageTitle :title="isNew ? 'Add new note' : 'Edit note'" class="text-left" />
            </div>
            <form class="flex flex-col gap-5 overflow-y-auto" @submit.prevent="handleSave">
                <BaseCard class="flex flex-col gap-3">
                    <p class="body-small">Notes</p>
                    <p class="body-small font-normal">The more I know about a person, the more helpful and personalized my insights will be.</p>
                    <div class="border border-control-border rounded-lg p-2 flex flex-col focus-field-within">
                        <BaseTextarea v-model:input="input" rows="6" class="font-normal mt-2" aria-label="Notes" placeholder="Type or speak here" />
                        <BaseButton
                            v-if="!isTranscribing"
                            theme="tertiary"
                            icon-only
                            class="self-end"
                            :aria-label="isRecording ? 'Stop recording note' : 'Start recording note'"
                            :aria-pressed="isRecording"
                            type="button"
                            @click.prevent.stop="handleRecord">
                            <i class="bi text-2xl/none" :class="isRecording ? 'bi-stop-circle' : 'bi-mic'" aria-hidden="true"></i>
                        </BaseButton>
                        <LoadingSpinner v-else class="w-6 h-6 self-end" />
                    </div>
                    <BaseNotification v-if="errorMessage" theme="error" hide-dismiss :text="errorMessage" />
                </BaseCard>
                <div class="flex items-center justify-end gap-3">
                    <BaseButton v-if="!isNew" theme="tertiary" icon-only aria-label="Delete note" @click="isConfirmDeleteDialogOpen = true">
                        <i class="bi bi-trash3 text-lg" aria-hidden="true"></i>
                    </BaseButton>
                    <BaseButton type="submit" theme="primary" class="w-fit body-small" :disabled="!isFormValid || isSaving">
                        <span v-if="!isSaving">{{ isNew ? "Add" : "Edit" }} Note</span>
                        <LoadingSpinner v-else class="w-6 h-6" />
                    </BaseButton>
                </div>
            </form>
        </div>
    </div>
    <ConfirmationDialog
        v-model:open="isConfirmDeleteDialogOpen"
        title="Delete note?"
        text="Are you sure you want to delete this note?"
        confirm-label="Delete"
        @confirm="handleDelete" />
</template>

<script>
import BaseNotification from "~vue/components/BaseNotification.vue";
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup>
import { AudioRecorder } from "/js/AudioRecorder";
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BasePageTitle from "~vue/components/BasePageTitle.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { useRecorder } from "~vue/composables/useRecorder";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { logUserInteraction } from "~vue/utils/logUtils";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { computed, inject, onMounted, onUnmounted, ref } from "vue";

const { $setEventContext } = inject("globalProperties");

const { connection_id, event_context, connection_detail_url, connections_page_url, note } = defineProps({
    connection_id: { type: String, required: true },
    connections_page_url: { type: String, required: true },
    connection_detail_url: { type: String, default: undefined },
    event_context: { type: Object, required: true },
    note: { type: Object, required: true },
});

const recorder = ref(new AudioRecorder());
const { isTranscribing, isRecording, handleRecord, input } = useRecorder({ recorder: recorder.value });

const isNew = computed(() => !note || !note.raw_note);
const isFormValid = computed(() => input.value?.trim().length > 0);
const payload = computed(() => ({
    raw_note: input.value,
}));
const {
    error: createError,
    execute: executeCreate,
    onFetchResponse: onCreateResponse,
    isFetching: isCreating,
} = useFetch(`/api/connections/${connection_id}/notes/`, { immediate: false }).post(payload).json();

const {
    error: editError,
    execute: executeEdit,
    onFetchResponse: onEditResponse,
    isFetching: isEditing,
} = useFetch(`/api/connections/notes/${note?.id}/`, { immediate: false }).put(payload).json();

onCreateResponse(() => {
    router.visit(connection_detail_url);
});
onEditResponse(() => {
    router.visit(connection_detail_url);
});

const errorMessage = computed(() => {
    const error = createError.value || editError.value;
    return isSessionEndedError(error) ? null : error?.message;
});
const isSaving = computed(() => isCreating.value || isEditing.value);

const handleSave = () => {
    createError.value = "";
    editError.value = "";
    if (isNew.value) {
        executeCreate();
    } else {
        executeEdit();
    }
};

// Delete note
const isConfirmDeleteDialogOpen = ref(false);
const { execute: executeDelete, onFetchResponse: onDeleteResponse } = useFetch(`/api/connections/notes/${note?.id}/`, { immediate: false }).delete();

const handleDelete = () => {
    logUserInteraction("connection_delete_note_button_clicked");
    executeDelete();
};

onDeleteResponse(() => {
    logUserInteraction("connection_note_deleted");
    router.visit(connection_detail_url);
});

onMounted(() => {
    $setEventContext(event_context);
    if (!isNew.value) {
        input.value = note.raw_note;
    }
});

onUnmounted(() => {
    $setEventContext({});
});
</script>
